/**
 * HandTracker — 21 landmarks per hand, up to 2 hands, via MediaPipe Hand
 * Landmarker.
 *
 * Each hand is tagged 'Left' or 'Right' by the model. The tracker exposes
 * `getHand('left')` / `getHand('right')` returning the most recent set of
 * landmarks for that hand, and helper detectors:
 *
 *   - getPinchDistance('left')   — distance between thumbTip and indexTip
 *   - isPinching('left', 0.05)   — true if pinch < threshold
 *   - getFingerCurl('left', 'index') — 0..1 closed-ness of a finger
 */
import { BaseTracker } from './BaseTracker.js';
import {
  HAND_LANDMARKS,
  HAND_LANDMARK_COUNT,
  lookupLandmark,
  distance,
  angle
} from './landmarks.js';

const DEFAULT_CDN =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/vision_bundle.mjs';
const DEFAULT_WASM =
  'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18/wasm';
const DEFAULT_MODEL =
  'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task';

export class HandTracker extends BaseTracker {
  /**
   * @param {object} [opts]
   * @param {number} [opts.numHands=2]
   */
  constructor(opts = {}) {
    super();
    this.modelUrl = opts.modelUrl || DEFAULT_MODEL;
    this.libraryUrl = opts.libraryUrl || DEFAULT_CDN;
    this.wasmUrl = opts.wasmUrl || DEFAULT_WASM;
    this.numHands = opts.numHands || 2;

    this._landmarker = null;
    this._video = null;
    this._rafId = null;
  }

  async load() {
    if (this.isAvailable || this.isLoading) return;
    this.isLoading = true;
    try {
      const vision = await import(/* @vite-ignore */ this.libraryUrl);
      const { HandLandmarker, FilesetResolver } = vision;
      const fileset = await FilesetResolver.forVisionTasks(this.wasmUrl);
      this._landmarker = await HandLandmarker.createFromOptions(fileset, {
        baseOptions: { modelAssetPath: this.modelUrl, delegate: 'GPU' },
        runningMode: 'VIDEO',
        numHands: this.numHands
      });
      this.isAvailable = true;
    } catch (err) {
      this.lastError = err;
      throw err;
    } finally {
      this.isLoading = false;
    }
  }

  async start(videoElement) {
    if (this._destroyed) return;
    if (!this.isAvailable) await this.load();
    this._video = videoElement;
    this._loop();
  }

  stop() {
    if (this._rafId != null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(this._rafId);
    }
    this._rafId = null;
    if (this._landmarker && typeof this._landmarker.close === 'function') {
      try { this._landmarker.close(); } catch (_) { /* ignore */ }
    }
    this._landmarker = null;
    this.isAvailable = false;
  }

  _loop() {
    if (this._destroyed || !this._video || !this._landmarker) return;
    const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    try {
      if (this._video.readyState >= 2) {
        const result = this._landmarker.detectForVideo(this._video, now);
        if (result && result.landmarks && result.landmarks.length > 0) {
          // Build a { left, right } map keyed by handedness label.
          const hands = { left: null, right: null };
          for (let i = 0; i < result.landmarks.length; i++) {
            const cats = (result.handednesses && result.handednesses[i]) || [];
            const label = (cats[0] && cats[0].categoryName) || (i === 0 ? 'Right' : 'Left');
            const key = label.toLowerCase() === 'left' ? 'left' : 'right';
            hands[key] = result.landmarks[i];
          }
          this._emit({ hands, timestamp: now });
        }
      }
    } catch (_) { /* ignore frame errors */ }
    if (typeof requestAnimationFrame !== 'undefined') {
      this._rafId = requestAnimationFrame(() => this._loop());
    }
  }

  // ───────────────────────────────────────────────────────── public access

  /** Returns the 21-landmark array for one hand, or null if not detected. */
  getHand(side) {
    if (!this.latest || !this.latest.hands) return null;
    const key = side === 'right' ? 'right' : 'left';
    return this.latest.hands[key];
  }

  getLandmark(side, name) {
    const hand = this.getHand(side);
    if (!hand) return null;
    const idx = lookupLandmark(HAND_LANDMARKS, name);
    if (idx < 0 || idx >= HAND_LANDMARK_COUNT) return null;
    return hand[idx] || null;
  }

  /** Distance between thumbTip and indexTip on the given hand. */
  getPinchDistance(side) {
    const t = this.getLandmark(side, 'thumbTip');
    const i = this.getLandmark(side, 'indexTip');
    if (!t || !i) return Infinity;
    return distance(t, i);
  }

  isPinching(side, threshold = 0.05) {
    return this.getPinchDistance(side) < threshold;
  }

  /**
   * 0 = finger fully extended, 1 = finger fully curled.
   * Computed from the angle at the proximal interphalangeal joint.
   */
  getFingerCurl(side, finger) {
    const baseName = ({
      thumb:  ['thumbCMC',  'thumbMCP',  'thumbTip'],
      index:  ['indexMCP',  'indexPIP',  'indexTip'],
      middle: ['middleMCP', 'middlePIP', 'middleTip'],
      ring:   ['ringMCP',   'ringPIP',   'ringTip'],
      pinky:  ['pinkyMCP',  'pinkyPIP',  'pinkyTip']
    })[finger];
    if (!baseName) return 0;
    const a = this.getLandmark(side, baseName[0]);
    const b = this.getLandmark(side, baseName[1]);
    const c = this.getLandmark(side, baseName[2]);
    const ang = angle(a, b, c);
    // Map [180°, 90°] → [0, 1].
    if (ang === 0) return 0;
    return Math.max(0, Math.min(1, (180 - ang) / 90));
  }

  static get LANDMARKS() { return HAND_LANDMARKS; }
}
